import React, { ForwardedRef } from 'react';
import {
  CSSRulesFunction,
  FontSize,
  ResponsiveValue,
  Theme,
} from '../../theme/types';
import { useTheme } from '../../theme';
import { Text } from '../typography';
import { Color, Display, FontFamily, FontWeight } from '../../theme/tokens';
import { useFocus, useHover } from '../../hooks';
import { CSSInterpolation, CSSObject } from '@emotion/serialize';
import { omit } from '../../utils';

type HTMLProps = Omit<React.HTMLAttributes<HTMLElement>, keyof AppearanceProps>;

export enum LinkVariant {
  Text = 'Text',
  None = 'None',
}

interface AppearanceProps {
  size?: ResponsiveValue<FontSize>;
  variant?: LinkVariant;
  display?: ResponsiveValue<
    Display.Block | Display.InlineBlock | Display.InlineFlex
  >;
}

interface Props {
  disabled?: boolean;
}

interface InternalProps {
  hovered: boolean;
  focused: boolean;
}

export interface LinkProps extends AppearanceProps, Props, HTMLProps {
  href?: HTMLAnchorElement['href'];
  target?: HTMLAnchorElement['href'];
}

enum LinkState {
  base = 'base',
  hover = 'hover',
  disabled = 'disabled',
  focused = 'focused',
}

const linkStateStyles = (
  theme: Theme,
  variant?: LinkVariant
): Record<LinkState, CSSObject> => {
  if (variant === LinkVariant.None) {
    return {
      base: {},
      hover: {},
      disabled: {
        '&:hover': {},
      },
      focused: {
        outline: 'none',
        boxShadow: `0 0 0 3px ${theme.color(Color.Secondary_600)}`,
      },
    };
  }

  return {
    base: {
      boxShadow: `0 1px 0 0 ${theme.color(Color.Secondary_400)}`,
    },
    hover: {
      boxShadow: `0 1px 0 0 ${theme.color(Color.Secondary_600)}`,
    },
    disabled: {
      boxShadow: `0 1px 0 0 ${theme.color(Color.NeutralGrey_200)}`,
      '&:hover': {
        cursor: 'not-allowed',
        boxShadow: 'none',
      },
      '&:focus': {
        boxShadow: 'none',
        outline: 'none',
      },
    },
    focused: {
      outline: 'none',
      boxShadow: `0 0 0 3px ${theme.color(Color.Secondary_600)}`,
      ...theme.radius(1.5),
    },
  };
};

export const anchorReset: CSSInterpolation = {
  color: 'inherit',
  textDecoration: 'auto',
  '&:hover': {
    color: 'inherit',
    textDecoration: 'auto',
    cursor: 'pointer',
  },
};

export const buttonReset: CSSInterpolation = {
  border: 'none',
  margin: 0,
  padding: 0,
  width: 'auto',
  textAlign: 'inherit',
  overflow: 'visible',
  background: 'transparent',
  color: 'inherit',
  font: 'inherit',
  lineHeight: 'normal',
  fontSmoothing: 'inherit',
  appearance: 'none',
  borderRadius: 'unset',
  '&::-moz-focus-inner': {
    border: 0,
    padding: 0,
  },
};

const createLinkStyles = (resetStyles: CSSInterpolation) => {
  const linkStyle: CSSRulesFunction<LinkProps & Props> = (theme, props) => {
    const { disabled, variant } = props;
    const stateStyles = linkStateStyles(theme, variant);

    const display = theme.responsiveStyle(props.display || Display.InlineFlex, [
      'display',
    ]);

    return [
      resetStyles,
      display,
      {
        width: 'auto',
        height: 'auto',
        cursor: 'pointer',
      },
      stateStyles.base,
      {
        '&:hover': {
          ...stateStyles.hover,
        },
        '&:focus': {
          ...stateStyles.focused,
        },
        ...(disabled && {
          ...stateStyles.disabled,
        }),
      },
    ];
  };

  return linkStyle;
};

const linkButtonStyle: CSSRulesFunction<LinkProps & Props> = (theme, props) => {
  return createLinkStyles(buttonReset)(theme, props);
};

const linkAnchorStyle: CSSRulesFunction<LinkProps & Props> = (theme, props) => {
  return createLinkStyles(anchorReset)(theme, props);
};

const linkTextColor = (
  props: Omit<LinkProps, 'href'> & InternalProps
): Color => {
  const { hovered, focused, disabled } = props;

  switch (true) {
    case disabled: {
      return Color.NeutralGrey_200;
    }
    case focused:
    case hovered: {
      return Color.Secondary_600;
    }
    default: {
      return Color.Secondary_400;
    }
  }
};

const disableAnchorLink = (disabled?: boolean) =>
  disabled && {
    onClick: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) =>
      e.preventDefault(),
  };

const Link = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, LinkProps>(
  function Link(props, ref) {
    const {
      onFocus,
      onBlur,
      children,
      size,
      disabled,
      variant = LinkVariant.Text,
      href,
      target,
    } = props;

    const { theme } = useTheme();
    const { handleFocus, handleBlur, focused } = useFocus(onFocus, onBlur);
    const { handleMouseEnter, handleMouseLeave, hovered } = useHover();

    const rest = omit(props, [
      'onFocus',
      'onBlur',
      'children',
      'size',
      'variant',
      'display',
      'href',
      'target',
    ]);

    const content = (
      <>
        {variant === LinkVariant.None && children}
        {variant === LinkVariant.Text && (
          <Text
            fontFamily={FontFamily.SemiBold}
            size={size || 2}
            color={linkTextColor({ focused, hovered, disabled })}
            fontWeight={FontWeight.SemiBold}
          >
            {children}
          </Text>
        )}
      </>
    );

    if (!href) {
      return (
        <button
          css={linkButtonStyle(theme, props)}
          ref={ref as ForwardedRef<HTMLButtonElement>}
          aria-disabled={disabled}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          {...rest}
        >
          {content}
        </button>
      );
    }

    return (
      <a
        onFocus={handleFocus}
        onBlur={handleBlur}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        css={linkAnchorStyle(theme, props)}
        ref={ref as ForwardedRef<HTMLAnchorElement>}
        aria-disabled={disabled}
        href={href}
        target={target}
        {...rest}
        {...disableAnchorLink(disabled)}
      >
        {content}
      </a>
    );
  }
);

export default Link;
